import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://rank5.io";
const SHARE_IMAGE = `${SITE_URL}/rank5-share.png`;

interface PageMetadata {
  title: string;
  socialTitle?: string;
  description: string;
  canonicalPath?: string;
  indexable: boolean;
}

const HOME_METADATA: PageMetadata = {
  title: "Rank5 | Online Party Game for Friends, Couples & Groups",
  socialTitle: "Rank5.io — Find out what your friends think of you",
  description:
    "Play Rank5, the online party game for friends, couples, families, classrooms and streamers. Rank cards, predict answers and see who knows whom best.",
  canonicalPath: "/",
  indexable: true,
};

function getPageMetadata(pathname: string): PageMetadata {
  if (pathname === "/") return HOME_METADATA;

  if (pathname === "/howTo") {
    return {
      title: "How to Play Rank5 | Online Party Game Rules",
      description:
        "Learn how to play Rank5: choose decks, rank five cards, predict another player's order and score points by showing how well you know your group.",
      canonicalPath: "/howTo",
      indexable: true,
    };
  }

  if (pathname === "/faq") {
    return {
      title: "Rank5 FAQ | Online Party Game Questions",
      description:
        "Answers about playing Rank5 with friends and groups, choosing card decks, creating your own decks, reporting cards and getting a game started.",
      canonicalPath: "/faq",
      indexable: true,
    };
  }

  if (pathname === "/community") {
    return {
      title: "Rank5 Community | Decks, Rankings & Player Insights",
      description:
        "Explore Rank5 community decks, player ranking insights, creator leaderboards and drawings made in this online party game.",
      canonicalPath: "/community",
      indexable: true,
    };
  }

  if (pathname === "/deck-tutorial") {
    return {
      title: "Create a Rank5 Card Deck | Deck Tutorial",
      description:
        "Learn how to write questions, answers and friend cards for a custom Rank5 party game deck.",
      canonicalPath: "/deck-tutorial",
      indexable: true,
    };
  }

  return {
    title: "Rank5 | Online Party Game",
    description:
      "Rank cards, predict each other's answers and discover who really knows whom best in Rank5.",
    indexable: false,
  };
}

function updateMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

/**
 * Keeps canonical, search and social metadata correct after client-side route
 * changes. Public content gets a stable canonical URL; game/session routes are
 * explicitly kept out of search results.
 */
export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const page = getPageMetadata(location.pathname);
    const canonicalUrl = page.canonicalPath
      ? `${SITE_URL}${page.canonicalPath}`
      : undefined;

    document.title = page.title;
    updateMeta("name", "description", page.description);
    updateMeta(
      "name",
      "robots",
      page.indexable
        ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        : "noindex, nofollow"
    );
    updateMeta("property", "og:title", page.socialTitle || page.title);
    updateMeta("property", "og:description", page.description);
    updateMeta("property", "og:image", SHARE_IMAGE);
    updateMeta("property", "og:url", canonicalUrl || `${SITE_URL}${location.pathname}`);
    updateMeta("name", "twitter:title", page.socialTitle || page.title);
    updateMeta("name", "twitter:description", page.description);
    updateMeta("name", "twitter:image", SHARE_IMAGE);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
    } else {
      canonical?.remove();
    }
  }, [location.pathname]);

  return null;
}
